import {
  BAN_DEFAULT_SECONDS,
  BanRequest,
  IpAddress,
  type ActionPreview,
  type ActionTarget,
  type CreateActionInput,
  type ServerGroupRef,
} from '@bartizo/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { useNavigate } from 'react-router';
import { createAction, previewAction } from '../api/actions.js';
import { approveSuggestion } from '../api/ai.js';
import { useCurrentUser } from '../auth/session-context.js';
import { BAN_DURATIONS, SKIP_LABEL } from '../lib/actions.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Dialog } from '../ui/Dialog.js';
import { SelectField, TextField } from '../ui/Field.js';

/**
 * Banning and unbanning an address (knowledge doc section 12, step 1).
 *
 * Section 12 puts both in the **Medium** row — an operator, after a confirm dialog
 * — and asks for a preview before anything that reaches more than one server. So
 * this is two steps, always: the form, then what the API says would happen —
 * which servers get the command, which are skipped and why, and the warnings — and
 * only then the button that does it. The preview is the API's own answer from the
 * same code as the action, not a guess made here; the real request is checked
 * again whatever the preview said.
 *
 * No password re-check: that is section 12's High row. The dialog is the Medium
 * row's protection, and it is not skippable.
 *
 * **Approving an AI suggestion is this dialog too** (P9.3): the address is the
 * suggestion's and cannot be changed, the proposal's duration and reason are filled
 * in, the target is every server unless narrowed, and the same preview comes
 * before anything is sent. The request goes to the approve route, which makes the
 * same ban.
 */

type Scope = 'server' | 'group' | 'all';

export interface ActionDialogServer {
  id: string;
  name: string;
  group: ServerGroupRef | null;
}

/** A proposed ban being approved: what the suggestion proposed. */
export interface SuggestionApproval {
  id: string;
  ip: string;
  durationSeconds: number;
  reason: string;
}

export interface ActionDialogProps {
  type: 'ban_ip' | 'unban_ip';
  /** The server it was opened from; without one (an address's page, P11.1) it is every server. */
  server?: ActionDialogServer;
  /** An event's source address, when the dialog was opened from one. */
  initialIp?: string;
  /** Set when the dialog approves an AI suggestion's ban. */
  suggestion?: SuggestionApproval;
  onClose: () => void;
}

const errorText = (error: unknown): string =>
  error instanceof Error ? error.message : 'Something went wrong; try again.';

export function ActionDialog({
  type,
  server,
  initialIp,
  suggestion,
  onClose,
}: ActionDialogProps): ReactNode {
  const session = useCurrentUser();
  const isAdmin = session.user.role === 'admin';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const banning = type === 'ban_ip';

  const [ip, setIp] = useState(suggestion?.ip ?? initialIp ?? '');
  const [duration, setDuration] = useState(
    String(suggestion?.durationSeconds ?? BAN_DEFAULT_SECONDS),
  );
  const [reason, setReason] = useState(suggestion?.reason ?? '');
  const [scope, setScope] = useState<Scope>(
    suggestion === undefined && server !== undefined ? 'server' : 'all',
  );
  const durations =
    suggestion === undefined ||
    BAN_DURATIONS.some((choice) => choice.seconds === suggestion.durationSeconds)
      ? BAN_DURATIONS
      : [
          {
            seconds: suggestion.durationSeconds,
            label: `${String(Math.round(suggestion.durationSeconds / 3600))} hours (as suggested)`,
          },
          ...BAN_DURATIONS,
        ];
  const [allowPrivate, setAllowPrivate] = useState(false);
  /** P11.9.3: a challenge instead, where a server's bouncer can show one. */
  const [remediation, setRemediation] = useState<'ban' | 'captcha'>('ban');
  const [problems, setProblems] = useState<{ ip?: string; reason?: string }>({});
  const [preview, setPreview] = useState<{ body: CreateActionInput; answer: ActionPreview }>();

  const target = (): ActionTarget =>
    scope === 'all' || server === undefined
      ? { kind: 'all' }
      : scope === 'group' && server.group !== null
        ? { kind: 'group', id: server.group.id }
        : { kind: 'server', id: server.id };

  const review = useMutation({
    mutationFn: async (body: CreateActionInput) => ({ body, answer: await previewAction(body) }),
    onSuccess: setPreview,
  });

  const act = useMutation({
    mutationFn: async (body: CreateActionInput): Promise<string> => {
      if (suggestion === undefined || body.type !== 'ban_ip') return (await createAction(body)).id;
      const { ip: _ip, ...args } = body.args;
      const approved = await approveSuggestion(suggestion.id, { ...args, target: body.target });
      return approved.actionId;
    },
    onSuccess: (actionId) => {
      void queryClient.invalidateQueries({ queryKey: ['actions'] });
      void queryClient.invalidateQueries({ queryKey: ['suggestions'] });
      void navigate(`/actions/${encodeURIComponent(actionId)}`);
    },
  });

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    const address = ip.trim();
    if (!IpAddress.safeParse(address).success) {
      setProblems({ ip: 'Enter one IPv4 or IPv6 address, like 198.51.100.7' });
      return;
    }
    let body: CreateActionInput;
    if (banning) {
      const args = BanRequest.safeParse({
        ip: address,
        duration_seconds: Number(duration),
        reason,
        allow_private: allowPrivate,
        remediation,
      });
      if (!args.success) {
        setProblems({ reason: 'Say why, in up to 120 characters on one line' });
        return;
      }
      body = { type: 'ban_ip', target: target(), args: args.data };
    } else {
      body = { type: 'unban_ip', target: target(), args: { ip: address } };
    }
    setProblems({});
    review.mutate(body);
  };

  const verb = banning ? 'Ban' : 'Unban';

  if (preview !== undefined) {
    const { answer, body } = preview;
    const shown = answer.servers.slice(0, 10);
    return (
      <Dialog
        title={`${verb} ${ip.trim()}?`}
        description="What the API says would happen. Nothing has been sent yet."
        onClose={onClose}
      >
        <div className="stack">
          {answer.servers.length === 0 ? (
            <Alert tone="warning" title="No server in that target can run this">
              Every one is skipped; the reasons are below.
            </Alert>
          ) : (
            <div>
              <p>
                Sent to{' '}
                {answer.servers.length === 1
                  ? '1 server'
                  : `${String(answer.servers.length)} servers`}
                , each with 15 minutes to collect it:
              </p>
              <ul className="text-sm">
                {shown.map((each) => (
                  <li key={each.serverId}>
                    {each.name}
                    {each.serverStatus === 'active' ? '' : ` (${each.serverStatus})`}
                  </li>
                ))}
                {answer.servers.length > shown.length && (
                  <li className="muted">and {answer.servers.length - shown.length} more</li>
                )}
              </ul>
            </div>
          )}

          {answer.skipped.length > 0 && (
            <div>
              <p>Skipped:</p>
              <ul className="text-sm">
                {answer.skipped.slice(0, 10).map((each) => (
                  <li key={each.serverId}>
                    {each.name}: {SKIP_LABEL[each.reason]}
                  </li>
                ))}
                {answer.skipped.length > 10 && (
                  <li className="muted">and {answer.skipped.length - 10} more</li>
                )}
              </ul>
            </div>
          )}

          {answer.warnings.length > 0 && (
            <Alert tone="warning" title="Before you go ahead">
              <ul>
                {answer.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </Alert>
          )}

          {act.isError && <Alert tone="danger">{errorText(act.error)}</Alert>}

          <div className="dialog-actions">
            <Button
              onClick={() => {
                setPreview(undefined);
                act.reset();
              }}
            >
              Back
            </Button>
            <Button
              variant={banning ? 'danger' : 'primary'}
              busy={act.isPending}
              busyLabel="Sending…"
              disabled={answer.servers.length === 0}
              onClick={() => {
                act.mutate(body);
              }}
            >
              {verb} on{' '}
              {answer.servers.length === 1
                ? '1 server'
                : `${String(answer.servers.length)} servers`}
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      title={
        suggestion !== undefined
          ? 'Approve the suggested ban'
          : banning
            ? 'Ban an address'
            : 'Unban an address'
      }
      description={
        banning
          ? 'CrowdSec on each server blocks it until the ban runs out. You will see what would happen before anything is sent.'
          : 'Removes the CrowdSec decisions for this address. You will see what would happen before anything is sent.'
      }
      onClose={onClose}
    >
      <form className="stack" onSubmit={submit} noValidate>
        <TextField
          label="Address"
          readOnly={suggestion !== undefined}
          hint={
            suggestion === undefined ? undefined : 'The incident’s address; it cannot be changed.'
          }
          autoFocus={initialIp === undefined && suggestion === undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder="198.51.100.7"
          value={ip}
          error={problems.ip ?? null}
          onChange={(event) => {
            setIp(event.target.value);
          }}
        />

        {banning && (
          <>
            {suggestion === undefined && (
              <SelectField
                label="Do"
                hint="A challenge asks the visitor to prove they are a person. Only servers whose web bouncer has captcha keys can show one; the others are skipped."
                value={remediation}
                onChange={(event) => {
                  setRemediation(event.target.value === 'captcha' ? 'captcha' : 'ban');
                }}
              >
                <option value="ban">Ban</option>
                <option value="captcha">Challenge (captcha)</option>
              </SelectField>
            )}
            <SelectField
              label="For"
              value={duration}
              onChange={(event) => {
                setDuration(event.target.value);
              }}
            >
              {durations.map((choice) => (
                <option key={choice.seconds} value={String(choice.seconds)}>
                  {choice.label}
                </option>
              ))}
            </SelectField>
            <TextField
              label="Reason"
              hint="Stored with the decision as “bartizo: your address: this reason”, so the server says who asked."
              autoFocus={initialIp !== undefined}
              maxLength={120}
              value={reason}
              error={problems.reason ?? null}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
          </>
        )}

        <SelectField
          label="On"
          value={scope}
          onChange={(event) => {
            setScope(event.target.value as Scope);
          }}
        >
          {server !== undefined && <option value="server">{server.name} only</option>}
          {server?.group != null && (
            <option value="group">Every server in {server.group.name}</option>
          )}
          <option value="all">Every server</option>
        </SelectField>

        {banning && isAdmin && (
          <label className="checkbox">
            <input
              type="checkbox"
              checked={allowPrivate}
              onChange={(event) => {
                setAllowPrivate(event.target.checked);
              }}
            />
            Allow a private address (admins only; usually a load balancer or a machine of your own)
          </label>
        )}

        {review.isError && <Alert tone="danger">{errorText(review.error)}</Alert>}

        <div className="dialog-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" busy={review.isPending} busyLabel="Checking…">
            Review
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
